import React, { useState, useRef, useEffect, useMemo } from 'react';
import './PostCarousel.css';
import { getFirstMediaUrl, isVideoFile } from '../../../utils/mediaHelpers.js';
import iconVideo from '../../../assets/images/icons/icon-video-generator.svg';
import iconInstagram from '../../../assets/images/icons/icon-instagram.svg';
import API_CONFIG from '../../../config/api';
import useLanguage from '../../../hooks/useLanguage';

const getVersionIdentifier = (version) => (
  version?.version_id ||
  version?.id ||
  version?._id ||
  version?.version ||
  version?.versionId ||
  null
);

const normalizeMediaUrl = (url) => {
  if (!url) return null;
  if (typeof url !== 'string') return null;

  const trimmed = url.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('data:')) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  if (trimmed.startsWith('/')) return `${API_CONFIG.BASE_URL}${trimmed}`;

  return trimmed;
};

const extractUrlFromItem = (item) => {
  if (!item) return null;
  if (typeof item === 'string') return normalizeMediaUrl(item);

  if (typeof item === 'object') {
    const directFields = [
      'public_url',
      'publicUrl',
      'signed_url',
      'signedUrl',
      'download_url',
      'downloadUrl',
      'secure_url',
      'secureUrl',
      'media_url',
      'mediaUrl',
      'file_url',
      'fileUrl',
      'image_url',
      'imageUrl',
      'thumbnail_url',
      'thumbnailUrl',
      'url',
      'src',
      'path'
    ];

    for (const field of directFields) {
      if (item[field]) {
        const extracted = normalizeMediaUrl(item[field]);
        if (extracted) return extracted;
      }
    }

    if (item.asset?.url) {
      const extracted = normalizeMediaUrl(item.asset.url);
      if (extracted) return extracted;
    }

    if (item.data) {
      const extracted = extractUrlFromItem(item.data);
      if (extracted) return extracted;
    }

    if (item.attributes) {
      const extracted = extractUrlFromItem(item.attributes);
      if (extracted) return extracted;
    }

    if (Array.isArray(item.sources)) {
      for (const source of item.sources) {
        const extracted = extractUrlFromItem(source);
        if (extracted) return extracted;
      }
    }

    if (item.formats && typeof item.formats === 'object') {
      for (const format of Object.values(item.formats)) {
        const extracted = extractUrlFromItem(format);
        if (extracted) return extracted;
      }
    }

    if (item.file) {
      const extracted = extractUrlFromItem(item.file);
      if (extracted) return extracted;
    }

    if (item.preview_url || item.previewUrl) {
      const extracted = normalizeMediaUrl(item.preview_url || item.previewUrl);
      if (extracted) return extracted;
    }
  }

  return null;
};

const collectMediaUrls = (source) => {
  if (!source) return [];

  const candidates = [];
  const fields = [
    'media_urls',
    'mediaUrls',
    'images',
    'media',
    'medias',
    'media_files',
    'mediaFiles',
    'generated_images',
    'generatedImages',
    'image_urls',
    'imageUrls',
    'assets',
    'attachments',
    'gallery',
    'mediaGallery',
    'preview_images',
    'previewImages',
    'thumbnails',
    'thumbnail'
  ];

  const pushValue = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      candidates.push(...value);
      return;
    }
    candidates.push(value);
  };

  fields.forEach((field) => {
    pushValue(source[field]);
  });

  pushValue(source.media_url);
  pushValue(source.mediaUrl);
  pushValue(source.image_url);
  pushValue(source.imageUrl);
  pushValue(source.image);
  pushValue(source.cover_image);
  pushValue(source.coverImage);
  pushValue(source.thumbnail_url);
  pushValue(source.thumbnailUrl);
  pushValue(source.preview_image_url);
  pushValue(source.previewImageUrl);
  pushValue(source.preview_url);
  pushValue(source.previewUrl);
  pushValue(source.current_version);
  pushValue(source.currentVersion);
  pushValue(source.active_version);
  pushValue(source.activeVersion);
  pushValue(source.definitive_version);
  pushValue(source.definitiveVersion);

  pushValue(source.attachments);

  if (source.data) {
    pushValue(source.data);
  }

  if (source.attributes) {
    pushValue(source.attributes);
  }

  const seen = new Set();
  const urls = [];

  candidates.forEach((candidate) => {
    const url = extractUrlFromItem(candidate);
    if (url && !seen.has(url)) {
      seen.add(url);
      urls.push(url);
    }
  });

  return urls;
};

const getActiveVersion = (post) => {
  if (!post) return null;

  const versions = Array.isArray(post.versions) ? post.versions : [];
  const currentVersionId = post.current_version_id || post.currentVersionId || post.active_version_id;

  if (currentVersionId) {
    const match = versions.find((version) => getVersionIdentifier(version) === currentVersionId);
    if (match) return match;
  }

  if (post.current_version) {
    return post.current_version;
  }

  if (versions.length > 0) {
    return versions[0];
  }

  return null;
};

const getPostPreviewData = (post) => {
  const activeVersion = getActiveVersion(post);
  const versionMedia = collectMediaUrls(activeVersion);
  const fallbackMedia = collectMediaUrls(post);

  const mediaUrls = versionMedia.length > 0 ? versionMedia : fallbackMedia;
  const title = post?.title || activeVersion?.title || activeVersion?.heading || activeVersion?.caption || post?.caption || '';
  const content = activeVersion?.content ?? activeVersion?.caption ?? post?.content ?? post?.caption ?? post?.description ?? '';

  return {
    mediaUrls,
    title,
    content
  };
};

const PostCarousel = ({ posts = [], isLoading = false, error = null }) => {
  const { t } = useLanguage();
  const orderedPosts = useMemo(() => {
    if (!Array.isArray(posts)) return [];

    const parseDate = (value) => {
      if (!value) return Number.POSITIVE_INFINITY;

      try {
        if (typeof value === 'object') {
          if (value.$date) {
            return new Date(value.$date).getTime();
          }
          if (value.seconds) {
            return Number(value.seconds) * 1000;
          }
        }

        return new Date(value).getTime();
      } catch (error) {
        console.warn('[PostCarousel] Failed to parse schedule date', value, error);
        return Number.POSITIVE_INFINITY;
      }
    };

    const parseCombinedDateTime = (dateValue, timeValue) => {
      if (!dateValue && !timeValue) return Number.POSITIVE_INFINITY;

      const datePart = (() => {
        if (!dateValue) return null;
        if (typeof dateValue === 'object' && dateValue.$date) return dateValue.$date;
        return dateValue;
      })();

      const timePart = (() => {
        if (!timeValue) return '00:00';
        if (typeof timeValue === 'object' && timeValue.$date) return new Date(timeValue.$date).toISOString().substring(11, 16);
        if (typeof timeValue === 'object' && typeof timeValue.seconds === 'number') {
          const date = new Date(Number(timeValue.seconds) * 1000);
          return `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`;
        }
        return timeValue;
      })();

      if (!datePart) return Number.POSITIVE_INFINITY;

      const normalizedTime = timePart.length === 5 ? `${timePart}:00` : timePart;
      const isoCandidate = `${datePart}T${normalizedTime}`;

      return parseDate(isoCandidate);
    };

    const resolveTimestamp = (post) => {
      if (!post) return Number.POSITIVE_INFINITY;

      const combinedCandidates = [
        [post.scheduled_date, post.scheduled_time],
        [post.scheduledDate, post.scheduledTime],
        [post.publish_date, post.publish_time],
        [post.publishDate, post.publishTime]
      ];

      for (const [dateValue, timeValue] of combinedCandidates) {
        const combinedTs = parseCombinedDateTime(dateValue, timeValue);
        if (Number.isFinite(combinedTs)) {
          return combinedTs;
        }
      }

      const candidates = [
        post.scheduled_datetime,
        post.scheduled_datetime_iso,
        post.scheduled_at,
        post.scheduledDate,
        post.scheduled_time,
        post.scheduledTime,
        post.publish_at,
        post.publish_time,
        post.published_at,
        post.created_at,
        post.createdAt
      ];

      for (const candidate of candidates) {
        const ts = parseDate(candidate);
        if (Number.isFinite(ts)) {
          return ts;
        }
      }

      return Number.POSITIVE_INFINITY;
    };

    return [...posts]
      .map((post) => ({ post, timestamp: resolveTimestamp(post) }))
      .sort((a, b) => a.timestamp - b.timestamp)
      .map(({ post }) => post);
  }, [posts]);

  const totalSlides = orderedPosts.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const carouselRef = useRef(null);
  const containerRef = useRef(null);

  // Handle touch start
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setTranslateX(0);
  };

  // Handle touch move
  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const currentX = e.touches[0].clientX;
    const diff = currentX - startX;
    setTranslateX(diff);
  };

  // Handle touch end
  const handleTouchEnd = () => {
    if (!isDragging) return;

    setIsDragging(false);
    const threshold = 80; // Minimum swipe distance to trigger navigation

    if (Math.abs(translateX) > threshold) {
      if (translateX > 0 && currentIndex > 0) {
        // Swipe right - go to previous
        setCurrentIndex(prev => prev - 1);
      } else if (translateX < 0 && currentIndex < totalSlides - 1) {
        // Swipe left - go to next
        setCurrentIndex(prev => prev + 1);
      }
    }

    setTranslateX(0);
  };

  // Handle mouse events for desktop testing
  const handleMouseDown = (e) => {
    e.preventDefault(); // Prevent default to avoid text selection
    setIsDragging(true);
    setStartX(e.clientX);
    setTranslateX(0);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();

    const currentX = e.clientX;
    const diff = currentX - startX;
    setTranslateX(diff);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;

    setIsDragging(false);
    const threshold = 80;

    if (Math.abs(translateX) > threshold) {
      if (translateX > 0 && currentIndex > 0) {
        setCurrentIndex(prev => prev - 1);
      } else if (translateX < 0 && currentIndex < totalSlides - 1) {
        setCurrentIndex(prev => prev + 1);
      }
    }

    setTranslateX(0);
  };

  // Navigation functions
  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < totalSlides - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        goToPrevious();
        break;
      case 'ArrowRight':
        e.preventDefault();
        goToNext();
        break;
      default:
        break;
    }
  };

  // Reset index when posts change
  useEffect(() => {
    setCurrentIndex(0);
  }, [totalSlides]);

  // Add keyboard listeners
  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('keydown', handleKeyDown);
      return () => carousel.removeEventListener('keydown', handleKeyDown);
    }
  }, [currentIndex, totalSlides]);

  if (isLoading) {
    return (
      <div className="post-carousel-empty">
        <p>{t('strategy.posts.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="post-carousel-empty">
        <p>{error || t('strategy.posts.errors.load')}</p>
      </div>
    );
  }

  if (!orderedPosts || totalSlides === 0) {
    return (
      <div className="post-carousel-empty">
        <p>{t('strategy.posts.empty')}</p>
      </div>
    );
  }

  return (
    <div className="post-carousel" ref={containerRef}>
      {/* Carousel container */}
      <div
        className="post-carousel-container"
        ref={carouselRef}
        tabIndex="0"
        role="region"
        aria-label={t('strategy.posts.carouselAria', {
          current: currentIndex + 1,
          total: totalSlides
        })}
        style={{
          transform: `translateX(calc(-${currentIndex * 100}% + ${isDragging ? translateX + 'px' : '0px'}))`,
          transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {orderedPosts.map((post, index) => {
          const postKey = post?.id || post?._id || post?.post_id || post?.postId || index;
          const preview = getPostPreviewData(post);
          const fallbackVersionUrl = collectMediaUrls(getActiveVersion(post))[0];
          const fallbackPostUrl = collectMediaUrls(post)[0];
          const fallbackActiveFirst = normalizeMediaUrl(getFirstMediaUrl(getActiveVersion(post)));
          const fallbackPostFirst = normalizeMediaUrl(getFirstMediaUrl(post));
          const thumbnail = preview.mediaUrls[0]
            || fallbackVersionUrl
            || fallbackPostUrl
            || fallbackActiveFirst
            || fallbackPostFirst;
          const isVideo = isVideoFile(thumbnail);

          return (
            <div key={postKey} className="post-carousel-slide">
              <div className="post-carousel-item">
                {/* Post thumbnail */}
                <div className="post-thumbnail">
                  {thumbnail ? (
                    isVideo ? (
                      <div className="post-video-fallback">
                        <img src={iconVideo} alt={t('strategy.posts.alt.video')} className="video-fallback-icon" />
                      </div>
                    ) : (
                      <img src={thumbnail} alt={t('strategy.posts.alt.thumbnail')} className="post-thumbnail-image" />
                    )
                  ) : (
                    <div className="post-thumbnail-fallback" aria-hidden="true" />
                  )}
                </div>

                {/* Post header */}
                <div className="post-header">
                  {(post.platform || '').toLowerCase() === 'instagram' ? (
                    <span className="post-platform-icon" title={t('strategy.posts.alt.instagram')}>
                      <img src={iconInstagram} alt={t('strategy.posts.alt.instagram')} />
                    </span>
                  ) : (
                    <span className="post-platform">{post.platform}</span>
                  )}
                  <span className="post-title">{preview.title || post.title || t('strategy.posts.defaultTitle', { index: index + 1 })}</span>
                </div>

                {/* Post content */}
                <p className="post-content">{preview.content || t('strategy.posts.noContent')}</p>
              </div>
            </div>
          );
        })}
      </div>


      {/* Dots indicator - only show if more than one post */}
      {totalSlides > 1 && (
        <div className="carousel-dots">
          {orderedPosts.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={t('strategy.posts.goToPost', { number: index + 1 })}
            />
          ))}
        </div>
      )}

      {/* Post counter */}
      <div className="carousel-counter">
        {t('strategy.posts.counter', { current: currentIndex + 1, total: totalSlides })}
      </div>
    </div>
  );
};

export default PostCarousel;
